import React, { useState, useRef } from 'react';
import { Button, Alert, Label, Input } from 'reactstrap';
import { IconButton, Tooltip, CircularProgress } from '@mui/material';
import {
    AutoStories as AutoStoriesIcon,
    Edit as EditIcon
} from '@mui/icons-material';
import MarkdownPreviewModal from '../../../helpers/MarkdownPreviewModal/MarkdownPreviewModal';
import { formatDate } from './utils';

const ReleaseNotesForm = ({
    version,
    content = '',
    onVersionChange,
    onContentChange,
    onSave,
    onReset,
    isSaving,
    editingId,
    onImport,
    onPreview,
    fileInputRef,
}) => (
    <div className="mb-4 p-3 rounded">
        <Label for="releaseVersion" className="fw-bold">Версия</Label>
        <Input
            type="text"
            id="releaseVersion"
            value={version}
            onChange={onVersionChange}
            placeholder="например: 1.2.3 или 2.0.1.4"
            className="mb-2"
        />
        <Label for="releaseContent" className="fw-bold mt-2">Описание изменений (поддерживается Markdown)</Label>
        <Input
            type="textarea"
            id="releaseContent"
            className="form-control mb-2 text-input audio-recorder-target"
            rows="8"
            value={content}
            onChange={(e) => onContentChange(e.target.value)}
            placeholder="Опишите новые возможности, исправления и т.д. Можно использовать **жирный**, *курсив*, списки и т.п."
        />
        <div className="d-flex gap-2 align-items-center flex-wrap">
            <Button
                onClick={onSave}
                disabled={isSaving}
                className="dt1520-button active-dt1520-button blue-button-with-borders"
            >
                {isSaving ? 'Сохранение...' : (editingId ? 'Обновить' : 'Добавить')}
            </Button>
            <input
                type="file"
                ref={fileInputRef}
                accept=".txt,.md"
                onChange={onImport}
                style={{ display: 'none' }}
            />
            <Button
                onClick={() => fileInputRef.current.click()}
                className="ml-2 dt1520-button active-dt1520-button blue-button-with-borders"
            >
                Загрузить из файла
            </Button>
            <Button
                onClick={onPreview}
                disabled={!content || typeof content !== 'string' || !content.trim()}
                className="ml-2 dt1520-button active-dt1520-button default-button"
            >
                Предпросмотр Markdown
            </Button>
            {editingId && (
                <Button
                    onClick={onReset}
                    className="ml-2 dt1520-button active-dt1520-button default-button"
                >
                    Отменить редактирование
                </Button>
            )}
        </div>
        <small className="text-muted d-block mt-2">
            Вы можете ввести текст вручную, загрузить файл .txt/.md или использовать разметку Markdown.
        </small>
    </div>
);

const ReleaseNotesList = ({ items, isLoading, onEdit, onDelete, onPreview, deletingItems }) => {
    if (isLoading) {
        return <p className="text-muted"><CircularProgress className="mr-3" color="primary" size={25} title="Загрузка..." /></p>;
    }
    if (items.length === 0) {
        return <p className="text-muted">Нет добавленных Release Notes</p>;
    }
    return (
        <ul className="list-group">
            {items.map((item) => (
                <li key={item.id} className="list-group-item release-notes-group-item d-flex border justify-content-between align-items-center">
                    <div className="flex-grow-1">
                        <strong>Версия {item.version}</strong>
                        {item.createdAt && (
                            <span className="text-muted ml-2 small">
                                (добавлено {formatDate(item.createdAt)})
                            </span>
                        )}
                        <div className="mt-1 small text-secondary">
                            {item.content.length > 200 ? `${item.content.substring(0, 200)}...` : item.content}
                        </div>
                    </div>
                    <div className="ml-3 d-flex align-items-center">
                        <Tooltip title="Предпросмотр">
                            <IconButton
                                size="sm"
                                onClick={() => onPreview(`Release Notes ${item.version}`, item.content)}
                                disabled={deletingItems.has(item.id)}
                                draggable="false"
                            >
                                <AutoStoriesIcon className="view-result-icon" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Редактировать">
                            <IconButton
                                size="sm"
                                onClick={() => onEdit(item)}
                                disabled={deletingItems.has(item.id)}
                                draggable="false"
                            >
                                <EditIcon className="view-result-icon" />
                            </IconButton>
                        </Tooltip>
                        <Tooltip title="Удалить">
                            <IconButton
                                onClick={() => onDelete(item.id, item.version)}
                                size="small"
                                disabled={deletingItems.has(item.id)}
                            >
                                <span className="material-symbols-outlined icon delete-icon">
                                    {deletingItems.has(item.id) ? 'auto_delete' : 'delete'}
                                </span>
                            </IconButton>
                        </Tooltip>
                    </div>
                </li>
            ))}
        </ul>
    );
};

const ReleaseNotesManager = ({
    releaseNotesList,
    isLoading,
    onSave,          // функция (version, content, editingId) => Promise
    onDelete,        // (id, version) => Promise
    onEdit,          // (item) => void – заполняет форму
    editingId,
    version,
    content,
    onVersionChange,
    onContentChange,
    onResetForm,
    isSaving,
    deletingItems,
    message,
    messageType,
    onClearMessage,
}) => {
    const fileInputRef = useRef(null);
    const [previewState, setPreviewState] = useState({ open: false, title: '', content: '' });

    const handleImportFromFile = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (event) => {
            onContentChange(event.target.result);
        };
        reader.onerror = () => {
        };
        reader.readAsText(file, 'UTF-8');
        e.target.value = '';
    };

    const openMarkdownPreview = (title, markdownContent) => {
        setPreviewState({ open: true, title, content: markdownContent });
    };

    const closePreview = () => setPreviewState(prev => ({ ...prev, open: false }));

    const handleSave = () => {
        onSave(version, content, editingId);
    };

    return (
        <div className="action-item mt-4 p-3 border rounded" id="releaseNotesForm">
            <h6 className="mb-0">Release Notes</h6>
            <ReleaseNotesForm
                version={version}
                content={content}
                onVersionChange={onVersionChange}
                onContentChange={onContentChange}
                onSave={handleSave}
                onReset={onResetForm}
                isSaving={isSaving}
                editingId={editingId}
                onImport={handleImportFromFile}
                onPreview={() => openMarkdownPreview('Предпросмотр текущего описания', content)}
                fileInputRef={fileInputRef}
            />
            <ReleaseNotesList
                items={releaseNotesList}
                isLoading={isLoading}
                onEdit={onEdit}
                onDelete={onDelete}
                onPreview={openMarkdownPreview}
                deletingItems={deletingItems}
            />
            {message && (
                <Alert color={messageType === 'success' ? 'success' : 'danger'} className="mt-3">
                    {message}
                </Alert>
            )}
            <MarkdownPreviewModal
                isOpen={previewState.open}
                toggle={closePreview}
                title={previewState.title}
                content={previewState.content}
            />
        </div>
    );
};

export default ReleaseNotesManager;